
import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Send, Bot, X, ShoppingCart, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";

interface Message {
  role: "user" | "assistant";
  content: string;
  products?: Product[];
}

interface Product {
  id: number;
  name: string;
  description: string;
  price: string;
  image: string;
  link: string;
}

interface ChatAIProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Product data synchronized with the Shop page
const allProducts: Product[] = [
  {
    id: 1,
    name: "Grooming Lounge Greatest Pits Deodorant",
    description: "A natural deodorant that actually works. Free from aluminum, parabens, and harmful chemicals.",
    price: "$23.00",
    image: "/lovable-uploads/7553ea87-a17e-481c-b7fb-9548fb2ffd93.png",
    link: "https://murphyandmcneil.com/products/grooming-lounge-greatest-pits-deodorant?sca_ref=8468498.oO9QXbgAQO"
  },
  {
    id: 2,
    name: "Kinko Care Natural Deodorant",
    description: "100% natural and vegan deodorant that effectively neutralizes odor. Free from aluminum, alcohol, and baking soda.",
    price: "$18.00",
    image: "/lovable-uploads/a2ef7eb0-0c25-4d2e-885a-ecbd6e258b4a.png",
    link: "https://www.kinko.care/?ref=rlexraps"
  },
  {
    id: 3,
    name: "It Just Works Deodorant",
    description: "A natural deodorant that actually works. Free from aluminum, parabens, and harmful chemicals.",
    price: "$35.99",
    image: "/lovable-uploads/a38e4978-a37a-452c-a67e-140d9acc3d2e.png",
    link: "https://itjustworksdeodorant.com?sca_ref=8468108.vEEWUNzhD1"
  },
  {
    id: 4,
    name: "Skillano Beauty Laser Hair Removal",
    description: "Professional-grade at-home laser hair removal device. Safe for all skin types and perfect for underarm treatment.",
    price: "$179.99",
    image: "/lovable-uploads/c496d496-c3cc-4846-b006-d74eeb924c08.png",
    link: "https://skillanobeauty.com"
  },
  {
    id: 5,
    name: "Travel Safety Razor",
    description: "Premium travel safety razor with leather case by Parker. Perfect for a close, irritation-free shave.",
    price: "$29.99",
    image: "/lovable-uploads/8d7b1b4e-2a3c-48a9-bd1b-e8f1f403758c.png",
    link: "https://murphyandmcneil.com/products/travel-safety-razor-with-leather-case-a1r-by-parker?sca_ref=8468498.oO9QXbgAQO"
  },
  {
    id: 6,
    name: "Rockwell Double Edge Razor Blades",
    description: "High-quality, durable razor blades for a smooth, close shave without irritation or ingrown hairs.",
    price: "$12.49",
    image: "/lovable-uploads/2abc262b-2602-434c-9ab6-cbf39724a4b0.png",
    link: "https://murphyandmcneil.com/products/rockwell-double-edge-razor-blades?sca_ref=8468498.oO9QXbgAQO"
  },
  {
    id: 9,
    name: "Hey Humans Natural Deodorant",
    description: "Contains naturally derived ingredients. Aluminum-free, vegan and cruelty-free natural deodorant with essential oils. Available in multiple scents with sustainable packaging.",
    price: "$12.99",
    image: "/lovable-uploads/e3ece06c-b176-4067-a58f-bb4f1c09d65a.png",
    link: "https://www.heyhumansusa.com/?dt_id=2391110"
  }
];

const ChatAI = ({ open, onOpenChange }: ChatAIProps) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: "Hello! I'm your armpit health assistant. How can I help you today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const checkForSiteForSaleQuestion = (query: string): boolean => {
    const saleQuestionPatterns = [
      /is\s+this\s+site\s+(for\s+sale|available\s+for\s+purchase)/i,
      /can\s+i\s+(buy|purchase)\s+this\s+(site|website)/i,
      /how\s+much\s+(for|to\s+buy)\s+this\s+(site|website)/i,
      /interested\s+in\s+(buying|purchasing)\s+this\s+(site|website)/i,
      /who\s+owns\s+this\s+(site|website)/i,
      /is\s+the\s+(site|website|domain)\s+for\s+sale/i,
      /would\s+you\s+sell\s+this\s+(site|website)/i
    ];
    
    return saleQuestionPatterns.some(pattern => pattern.test(query));
  };

  const checkForProductQuestion = (query: string): boolean => {
    const productQuestionPatterns = [
      /what\s+products?\s+do\s+you\s+(have|offer|sell)/i,
      /show\s+me\s+(your|the)\s+products?/i,
      /(list|display)\s+(all|your)\s+products?/i,
      /what\s+(items|things|products?)\s+(can\s+i|are)\s+(buy|available|for\s+sale)/i,
      /products?\s+available/i
    ];
    
    return productQuestionPatterns.some(pattern => pattern.test(query));
  };

  const checkForProductRecommendation = (query: string): boolean => {
    const recommendationPatterns = [
      /what\s+products?\s+would\s+you\s+(recommend|suggest)/i,
      /recommend\s+(?:me\s+)?(?:some\s+)?products?/i,
      /suggest\s+(?:me\s+)?(?:some\s+)?products?/i,
      /what\s+(?:should|could)\s+i\s+(?:buy|get|use)/i,
      /best\s+products?\s+for/i,
      /which\s+products?\s+(?:would|should|could|are)/i,
      /help\s+(?:me\s+)?(?:with|find)/i,
      /looking\s+for/i,
      /need\s+(?:a|some|to)/i,
      /having\s+(?:issues|problems|trouble)/i,
      /suffering\s+from/i,
      /good\s+for/i
    ];
    
    return recommendationPatterns.some(pattern => pattern.test(query));
  };

  const findRelevantProducts = (query: string): Product[] => {
    if (checkForProductQuestion(query)) {
      return allProducts;
    }
    
    // Enhanced keyword matching system with categories and weighted scoring
    const keywords = [
      // Deodorant related
      { words: ["deodorant", "odor", "smell", "sweat", "antiperspirant", "natural", "aluminum-free", "vegan"], 
        products: [1, 2, 3, 9], 
        weight: 1.5 },
      
      // Sensitive skin/irritation issues
      { words: ["sensitive", "irritation", "rash", "allergy", "reaction", "itchy", "itch", "burning", "gentle"], 
        products: [2, 3, 9], 
        weight: 2.0 },
      
      // Hair removal
      { words: ["hair", "removal", "laser", "shave", "shaving", "razor", "cut", "trim"], 
        products: [4, 5, 6], 
        weight: 1.5 },
      
      // Eco & sustainability
      { words: ["eco", "sustainable", "environment", "friendly", "natural", "organic", "cruelty-free"], 
        products: [2, 3, 9], 
        weight: 1.0 },
      
      // Specific concerns
      { words: ["cyst", "bump", "lump", "ingrown", "infection", "hyperhidrosis", "excessive"], 
        products: [2, 3, 9], 
        weight: 2.0 },
      
      // Price points
      { words: ["cheap", "affordable", "budget", "inexpensive"], 
        products: [9, 6, 2], 
        weight: 0.8 },
      
      { words: ["premium", "high-end", "quality", "best"], 
        products: [3, 4, 1], 
        weight: 0.8 }
    ];

    const lowerQuery = query.toLowerCase();
    const productScores = new Map<number, number>();
    
    // Initialize all products with a base score
    allProducts.forEach(product => {
      productScores.set(product.id, 0);
    });
    
    // Score products based on keyword matches
    keywords.forEach(category => {
      let categoryMatchFound = false;
      
      category.words.forEach(word => {
        if (lowerQuery.includes(word)) {
          categoryMatchFound = true;
          category.products.forEach(id => {
            const currentScore = productScores.get(id) || 0;
            productScores.set(id, currentScore + category.weight);
          });
        }
      });
      
      // If no exact match but query might be related to this category
      if (!categoryMatchFound) {
        const querySplit = lowerQuery.split(/\s+/);
        category.words.forEach(word => {
          querySplit.forEach(queryWord => {
            // Check for partial matches or similar words
            if (word.includes(queryWord) || queryWord.includes(word)) {
              category.products.forEach(id => {
                const currentScore = productScores.get(id) || 0;
                productScores.set(id, currentScore + (category.weight * 0.5));
              });
            }
          });
        });
      }
    });
    
    // Convert to array, sort by score and filter out low-scoring products
    const scoredProducts = Array.from(productScores.entries())
      .filter(([_, score]) => score > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([id, _]) => allProducts.find(product => product.id === id))
      .filter((product): product is Product => product !== undefined);
    
    // If no matches found, return default recommendations
    if (scoredProducts.length === 0) {
      // Determine if query sounds health-related or product-seeking
      if (lowerQuery.match(/problem|issue|health|condition|treatment|remedy|cure|help/i)) {
        return allProducts.filter(p => [2, 3, 9].includes(p.id)); // Return gentle, natural options
      }
      return allProducts.slice(0, 3); // Default top 3 products
    }
    
    // Return top scoring products (max 3)
    return scoredProducts.slice(0, 3);
  };

  const formatMessageText = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const boldText = part.substring(2, part.length - 2);
        return <strong key={index}>{boldText}</strong>;
      }
      return <span key={index}>{part}</span>;
    });
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      role: "user",
      content: input,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      if (checkForSiteForSaleQuestion(input)) {
        const aiResponse: Message = {
          role: "assistant",
          content: "The Owner of this site would be open to a reasonable offer. Please email info@fijidigital.com for more information."
        };
        
        setMessages(prev => [...prev, aiResponse]);
        setIsLoading(false);
        return;
      }
      
      if (checkForProductQuestion(input)) {
        const productList = allProducts.map(p => p.name).join(", ");
        const aiResponse: Message = {
          role: "assistant",
          content: `We offer several products for armpit health and care. Here are all our current products: **${productList}**. Would you like more information about any specific product?`,
          products: allProducts
        };
        
        setMessages(prev => [...prev, aiResponse]);
        setIsLoading(false);
        return;
      }

      // Always check for recommendation intent to ensure we're catching all potential product queries
      const relevantProducts = findRelevantProducts(input);
      const shouldRecommendProducts = checkForProductRecommendation(input) || 
                                     relevantProducts.length > 0;
      
      if (shouldRecommendProducts) {
        const productNames = relevantProducts.map(p => p.name).join(", ");
        
        let responseContent = "";
        if (relevantProducts.length > 0) {
          responseContent = `Based on your question about "${input}", I would recommend: **${productNames}**. These products are specifically selected to address your needs. Would you like more details about any of these options?`;
        } else {
          responseContent = `Based on your question, I'd recommend checking out our product range for armpit health solutions. Here are some options that might help with your concerns.`;
        }
        
        const aiResponse: Message = {
          role: "assistant",
          content: responseContent,
          products: relevantProducts.length > 0 ? relevantProducts : allProducts.slice(0, 3)
        };
        
        setMessages(prev => [...prev, aiResponse]);
        setIsLoading(false);
        return;
      }
      
      // For all other queries, use the AI response
      const response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer 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",
        },
        body: JSON.stringify({
          model: "gpt-4o-mini",
          messages: [
            {
              role: "system",
              content: 
                "You are a helpful armpit health assistant. Your expertise is in armpit-related issues, natural deodorants, skin care for underarms, and general hygiene. Provide accurate, concise, and helpful responses related to armpit health. If asked about topics unrelated to armpit health, gently steer the conversation back to your area of expertise. Use **bold text** to emphasize important points. Always look for opportunities to recommend relevant products for the user's issues or questions."
            },
            ...messages.map(msg => ({
              role: msg.role,
              content: msg.content
            })),
            {
              role: "user",
              content: input
            }
          ],
          temperature: 0.7,
          max_tokens: 500,
        }),
      });

      const data = await response.json();
      
      if (data.choices && data.choices[0]?.message) {
        // Check the AI response for product recommendation opportunities
        const aiResponseText = data.choices[0].message.content;
        const relevantProducts = findRelevantProducts(input + " " + aiResponseText);
        
        // If the AI response contains product-related content, attach products
        const productMentioned = /product|deodorant|razor|shav(e|ing)|antiperspirant/i.test(aiResponseText);
        
        const aiMessage: Message = {
          role: "assistant",
          content: aiResponseText,
          products: (productMentioned || relevantProducts.length > 0) ? 
                    relevantProducts.length > 0 ? relevantProducts : allProducts.slice(0, 3) : 
                    undefined
        };
        
        setMessages((prev) => [...prev, aiMessage]);
      } else {
        throw new Error("Failed to get response from AI");
      }
    } catch (error) {
      console.error("Error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, I encountered an error. Please try again later.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToShop = () => {
    onOpenChange(false);
    navigate("/shop");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] h-[600px] max-h-[80vh] flex flex-col p-0">
        <DialogHeader className="p-4 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              Armpit Health Assistant
            </DialogTitle>
            <DialogClose className="h-4 w-4 opacity-70" />
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-10">
          {messages.map((message, index) => (
            <div key={index} className="space-y-8">
              <div
                className={`flex ${
                  message.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[85%] rounded-lg p-4 ${
                    message.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary"
                  }`}
                >
                  <div className="leading-relaxed text-balance tracking-wide space-y-5">
                    {formatMessageText(message.content)}
                  </div>
                </div>
              </div>
              
              {message.products && message.products.length > 0 && (
                <div className="mt-8 space-y-5">
                  <div className="text-sm font-medium text-muted-foreground">
                    Recommended products for your query:
                  </div>
                  <div className="space-y-5">
                    {message.products.map(product => (
                      <a 
                        key={product.id}
                        href={product.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block hover:no-underline"
                      >
                        <Card className="overflow-hidden hover:shadow-md transition-shadow">
                          <CardContent className="p-3">
                            <div className="flex items-center gap-3">
                              <div className="h-12 w-12 bg-mint-100 rounded-md overflow-hidden flex-shrink-0">
                                <img
                                  src={product.image}
                                  alt={product.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1">
                                  <h4 className="text-sm font-medium truncate">{product.name}</h4>
                                  <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                </div>
                                <p className="text-xs text-muted-foreground truncate">{product.price}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </a>
                    ))}
                    <Button
                      onClick={handleGoToShop}
                      variant="outline"
                      size="sm"
                      className="w-full flex items-center justify-center gap-1 mt-4"
                    >
                      <ShoppingCart className="h-4 w-4" />
                      <span>View all products</span>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-lg p-4 bg-secondary">
                <span className="inline-block animate-pulse">Thinking...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="p-4 border-t mt-auto">
          <div className="flex gap-2">
            <Input
              placeholder="Ask about armpit health..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              className="flex-1"
            />
            <Button 
              size="icon" 
              onClick={handleSend} 
              disabled={isLoading || !input.trim()}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChatAI;
